import React, {
  useContext,
  createContext,
  FC,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import {ReactQueryDevtools} from 'react-query/devtools';
import {
  isBulkRequest,
  LazyAsyncData,
  LazyAsyncDataValue,
  Result,
} from './types';
import {Config} from './useRequest';

// eslint-disable-next-line no-restricted-imports
import {QueryClient} from 'react-query';
import {QueryClientProvider} from './react-query';

export type InitialData = Map<string, Error | object>;

interface DataFetcherProviderInitialServerRenderProps {
  initialData: InitialData;
  isInitialServerRequest: false;
}

interface DataFetcherProviderGeneralProps {
  initialData: Map<string, LazyAsyncData>;
  isInitialServerRequest: true;
}

type DataFetcherProviderProps =
  | DataFetcherProviderInitialServerRenderProps
  | DataFetcherProviderGeneralProps;

export type InternalRequestConfig = Config<any>;

type DataFetcherContext = DataFetcherProviderProps & {
  addRequest: (
    key: string,
    nextData: LazyAsyncDataValue,
    config?: InternalRequestConfig,
  ) => void;
  deleteKey: (key: string) => void;
};

const DataFetcherContext = createContext<DataFetcherContext>({
  initialData: new Map(),
  isInitialServerRequest: false,
  addRequest: (): void => {
    throw new Error(
      'The default action should never be used, replace it using DataFetcherProvider',
    );
  },
  deleteKey: (): void => {
    throw new Error(
      'The default action should never be used, replace it using DataFetcherProvider',
    );
  },
});

export const DataFetcherProvider: FC<
  React.PropsWithChildren<DataFetcherProviderProps>
> = ({initialData, isInitialServerRequest, children}) => {
  const isDevToolActive = useDevToolsActive();
  const addRequest = useCallback(
    (
      key: string,
      nextData: LazyAsyncDataValue,
      config?: InternalRequestConfig,
    ) => {
      if (process.env.NODE_ENV !== 'production') {
        if (initialData.has(key) && !isBulkRequest(key)) {
          console.warn(
            `Tadarida: Key \`${key}\` already exists. This is not a problem but unexpected as it indicates that the same request is fired more than once per session which could lead to bad performance. This might happen if you render the same component with the same data or a cache key is not properly set up. Best check your usage of useRequest in all components.`,
          );
        }
      }
      if (!config?.lowPriority || !initialData.has(key)) {
        initialData.set(key, {
          response: nextData,
          config,
        });
      }
    },
    [initialData],
  );

  const deleteKey = useCallback(
    (key: string) => {
      initialData.delete(key);
    },
    [initialData],
  );

  const value = useMemo(
    () => ({
      initialData,
      isInitialServerRequest,
      addRequest,
      deleteKey,
    }),
    [initialData, isInitialServerRequest, addRequest, deleteKey],
  );

  return (
    <DataFetcherContext.Provider value={value as DataFetcherContext}>
      <QueryClientProviderUniversal>
        {children}
        {isDevToolActive && (
          <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
        )}
      </QueryClientProviderUniversal>
    </DataFetcherContext.Provider>
  );
};
const useDevToolsActive = () => {
  const [isActive, setIsActive] = useState(false);
  useEffect(() => {
    if (
      process.env.NODE_ENV !== 'production' &&
      process.env.NODE_ENV !== 'test'
    ) {
      // Check DEV_TOOLS Cookie or LS entry to enable react-query-dev tools
      if (
        localStorage.getItem('DEV_TOOLS') ||
        document.cookie.split(';').some(c => c.startsWith(' DEV_TOOLS='))
      ) {
        // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
        setIsActive(true);
      }
    }
  }, []);
  return isActive;
};

export const useDataFetcherInitialData = <T extends object = object>(
  key: string,
): Result<T> | undefined => {
  const context = useContext(DataFetcherContext);
  if (context.isInitialServerRequest) {
    return undefined;
  }

  return context.initialData.get(key) as Result<T>;
};

export const useIsInitialServerRequest = () => {
  return useContext(DataFetcherContext).isInitialServerRequest;
};

export const useDataFetcherAction = () => {
  const {addRequest, deleteKey} = useContext(DataFetcherContext);

  return {addRequest, deleteKey};
};

const QueryClientProviderUniversal: FC<React.PropsWithChildren> = ({
  children,
}) => {
  // on the server we don't need the react query and it created some memory issues, therefore we completely remove it here
  if (!process.browser) {
    return <>{children}</>;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [client] = useState(() => new QueryClient());
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
};
